/**
 * A machine's menu (ADR 0106 decision 2), the same on its card and in its drawer, showing only
 * what applies, in this order: for a VM Orgus created, its items first (decision 3: Stop VM or
 * Start VM, Restart VM, Keep this VM, Delete VM, from features/cloud), then Disable new work or Enable, Rename, Update agent (when a newer
 * release is offered), then Remove from Orgus. Disable asks for an optional reason in a small
 * dialog; Rename sets the display name. Errors show in the dialog, or through onFail.
 */
import { ArrowUpCircle, CirclePause, CirclePlay, Ellipsis, Pencil, ShieldOff } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { CloudVm, Machine } from '@orgus/protocol';
import { Button, Dialog, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, IconButton, Input, toUiError } from '@orgus/ui';
import { useSchedulerState } from '../scheduler/data.ts';
import { setMachineWork } from '../scheduler/NodeControls.tsx';
import { useVmMenu } from '../cloud/VmControls.tsx';
import { cleanMachineName, renameMachine, useMachineDrain } from './controls.ts';

/** A menu action that failed, for the page to say in its banner. */
export type MenuFailure = { what: string; error: unknown };

export interface MachineMenuItem {
  key: 'work' | 'rename' | 'update' | 'remove';
  label: string;
}

/** The items a machine's menu shows, in order (pure, so the order and the words are tested). */
export function machineMenuItems(m: Pick<Machine, 'name'>, o: { drained: boolean | undefined; updateTo: string | null }): MachineMenuItem[] {
  const items: MachineMenuItem[] = [];
  if (o.drained !== undefined) items.push({ key: 'work', label: o.drained ? 'Enable' : 'Disable new work' });
  items.push({ key: 'rename', label: 'Rename' });
  if (o.updateTo) items.push({ key: 'update', label: `Update agent to ${o.updateTo}` });
  items.push({ key: 'remove', label: 'Remove from Orgus…' });
  return items;
}

const ICON = { work: null, rename: <Pencil />, update: <ArrowUpCircle />, remove: <ShieldOff /> };

export function MachineMenu({
  machine: m,
  running,
  updateTo = null,
  onUpdate,
  onRemove,
  onFail,
  vm = null,
}: {
  machine: Machine;
  /** The VM Orgus created for it: its items go above the common ones. */
  vm?: CloudVm | null;
  /** Live sessions on it, for the toast after Disable. */
  running: number;
  updateTo?: string | null;
  onUpdate?: () => Promise<void>;
  onRemove: () => void;
  onFail: (f: MenuFailure) => void;
}) {
  // Seeds the demo scheduler in ?mock=1, so demo machines can be disabled too.
  useSchedulerState();
  const drain = useMachineDrain(m.id);
  const drained = drain === undefined ? undefined : !!drain;
  const [dialog, setDialog] = useState<'disable' | 'rename' | null>(null);
  const items = machineMenuItems(m, { drained, updateTo: onUpdate ? updateTo : null });
  const vmMenu = useVmMenu(vm, { running, onFail });

  const select = (key: MachineMenuItem['key']) => {
    if (key === 'work' && drained) void setMachineWork(m, true, running).catch((error: unknown) => onFail({ what: `${m.name} was not enabled`, error }));
    else if (key === 'work') setDialog('disable');
    else if (key === 'rename') setDialog('rename');
    else if (key === 'update') void onUpdate?.().catch((error: unknown) => onFail({ what: `${m.name} was not updated`, error }));
    else onRemove();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton label={`More for ${m.name}`} icon={<Ellipsis />} size="sm" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {vmMenu.items}
          {items.map((it) => (
            <span key={it.key} className="contents">
              {it.key === 'remove' && <DropdownMenuSeparator />}
              <DropdownMenuItem icon={it.key === 'work' ? drained ? <CirclePlay /> : <CirclePause /> : ICON[it.key]} danger={it.key === 'remove'} onSelect={() => select(it.key)}>
                {it.label}
              </DropdownMenuItem>
            </span>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {vmMenu.dialogs}
      <DisableDialog machine={m} running={running} open={dialog === 'disable'} onOpenChange={(v) => setDialog(v ? 'disable' : null)} />
      <RenameDialog machine={m} open={dialog === 'rename'} onOpenChange={(v) => setDialog(v ? 'rename' : null)} />
    </>
  );
}

function errorText(e: unknown): string {
  const u = toUiError(e);
  return [u.what, u.why].filter(Boolean).join(' ');
}

function DisableDialog({ machine: m, running, open, onOpenChange }: { machine: Machine; running: number; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await setMachineWork(m, false, running, reason.trim());
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      dismissible={!busy}
      title={`Disable new work on ${m.name}`}
      description={running ? `Its ${running} running ${running === 1 ? 'session finishes' : 'sessions finish'}. Nothing new starts until you enable it.` : 'Nothing new starts on it until you enable it.'}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<CirclePause />} loading={busy} onClick={() => void submit()}>
            Disable new work
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => void submit(e)}>
        <Input label="Reason (optional)" placeholder="Disk replacement" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} error={error ?? undefined} disabled={busy} />
      </form>
    </Dialog>
  );
}

function RenameDialog({ machine: m, open, onOpenChange }: { machine: Machine; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [name, setName] = useState(m.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName(m.name);
      setError(null);
    }
  }, [open, m.name]);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!cleanMachineName(name)) {
      setError('Type a name of 1 to 120 characters.');
      return;
    }
    setBusy(true);
    try {
      await renameMachine(m, name);
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      dismissible={!busy}
      title={`Rename ${m.name}`}
      description={m.hostname ? `Its agent still reports the host name ${m.hostname}.` : 'The name shows everywhere in Orgus.'}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} disabled={cleanMachineName(name) === m.name}>
            Rename machine
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => void submit(e)}>
        <Input label="Name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} error={error ?? undefined} disabled={busy} autoFocus />
      </form>
    </Dialog>
  );
}
